import { useEffect, useState } from 'react';

// ---------- settings ----------
const KEY = 'giss_gnss_cfg';
export const GNSS_DEFAULTS = { mode: 'system', baud: 9600, url: 'ws://192.168.42.1:9001', sim: false, device: 'Emlid Reach', antenna: 2, depth: 0, avg: 1, warn: 0.5, prec: 'survey', hrmsMax: 0.05, maxAge: 3, poleRef: 'antenna' };
const PREC_MIG = { warn: 'plan', strict: 'survey' };
export const BAUDS = [4800, 9600, 38400, 57600, 115200];
export const DEVICES = ['Emlid Reach', 'Leica Zeno', 'Trimble Catalyst', 'Garmin GLO', 'Bad Elf'];
export const getGnssCfg = () => { let c; try { c = { ...GNSS_DEFAULTS, ...JSON.parse(localStorage.getItem(KEY)) }; } catch { c = { ...GNSS_DEFAULTS }; } c.prec = PREC_MIG[c.prec] || c.prec; return c; };
export const setGnssCfg = (patch) => { const c = { ...getGnssCfg(), ...patch }; localStorage.setItem(KEY, JSON.stringify(c)); emit({ cfgTick: Date.now() }); return c; };

// GGA quality -> fix code
export const FIX_CODES = { 4: 'rtk', 5: 'float', 2: 'dgps', 1: 'gps', 0: 'none' };
export const FIX_COLORS = { rtk: '#16a34a', float: '#eab308', dgps: '#2563eb', gps: '#f97316', sys: '#f97316', none: '#dc2626' };
export const FIX_SHORT = { rtk: 'RTK FIX', float: 'RTK FLOAT', dgps: 'DGPS', gps: 'GPS', sys: 'GPS', none: '–' };
export const FIX_RANK = { rtk: 4, float: 3, dgps: 2, gps: 1, sys: 1, none: 0 };
const SYS = { GP: 'GPS', GL: 'GLONASS', GA: 'Galileo', GB: 'BeiDou', BD: 'BeiDou', GQ: 'QZSS' };
const GSA_SYS = { 1: 'GPS', 2: 'GLONASS', 3: 'Galileo', 4: 'BeiDou', 5: 'QZSS' };

// ---------- live state ----------
let state = { connected: false, mode: null, fix: null, lat: null, lon: null, alt: null, geoid: null, sats: null, hdop: null, pdop: null, vdop: null, hrms: null, vrms: null, systems: [], epoch: 0, ts: null, error: null, est: false };
const subs = new Set();
function emit(patch) { state = { ...state, ...patch }; subs.forEach((f) => f(state)); }
export const gnssState = () => state;
export const subscribeGnss = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export function useGnss() {
  const [s, setS] = useState(state);
  useEffect(() => subscribeGnss(setS), []);
  return s;
}
// pole height to subtract: 0 when the receiver already outputs the pole tip (tilt compensation)
export const poleOff = () => { const c = getGnssCfg(); return c.poleRef === 'tip' ? 0 : +c.antenna || 0; };
export const maxAgeMs = () => (+getGnssCfg().maxAge || 3) * 1000;
export const gnssLive = (s = state) => s.connected && s.lat != null && s.fix && s.fix !== 'none' && Date.now() - (s.ts || 0) < maxAgeMs();

// ---------- NMEA 0183 ----------
const dm = (v, h) => {
  if (!v) return null;
  const d = Math.floor(+v / 100), x = d + (+v - d * 100) / 60;
  return h === 'S' || h === 'W' ? -x : x;
};
const num = (v) => (v === '' || v == null || Number.isNaN(+v) ? null : +v);
export const nmeaChecksum = (body) => { let c = 0; for (let i = 0; i < body.length; i++) c ^= body.charCodeAt(i); return c.toString(16).toUpperCase().padStart(2, '0'); };

export function parseNmea(line) {
  const s = line.trim();
  if (!s.startsWith('$')) return null;
  const [body, cs] = s.slice(1).split('*');
  if (cs && nmeaChecksum(body) !== cs.slice(0, 2).toUpperCase()) return null;
  const f = body.split(',');
  const talker = f[0].slice(0, 2), type = f[0].slice(2);
  if (type === 'GGA') {
    const q = num(f[6]);
    return { lat: dm(f[2], f[3]), lon: dm(f[4], f[5]), fix: FIX_CODES[q] || (q ? 'gps' : 'none'), sats: num(f[7]), hdop: num(f[8]), alt: num(f[9]), geoid: num(f[11]), epoch: true };
  }
  if (type === 'RMC') return f[2] === 'A' ? { lat: dm(f[3], f[4]), lon: dm(f[5], f[6]), speed: num(f[7]) } : { fix: 'none' };
  if (type === 'GSA') {
    const sys = GSA_SYS[num(f[18])] || SYS[talker];
    return { pdop: num(f[15]), hdop: num(f[16]), vdop: num(f[17]), sys };
  }
  if (type === 'GSV') return { sys: SYS[talker] };
  if (type === 'GST') {
    const a = num(f[6]), b = num(f[7]);
    return { hrms: a != null && b != null ? Math.hypot(a, b) : null, vrms: num(f[8]) };
  }
  return null;
}

let buf = '';
export function feedNmea(text) {
  buf += text;
  const lines = buf.split(/\r?\n/);
  buf = lines.pop().slice(-512);
  lines.forEach((ln) => {
    const p = parseNmea(ln);
    if (!p) return;
    const { sys, epoch, ...rest } = p;
    const patch = Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null));
    if (sys && !state.systems.includes(sys)) patch.systems = [...state.systems, sys];
    if (epoch) { patch.epoch = state.epoch + 1; patch.ts = Date.now(); }
    emit({ ...patch, est: false });
  });
}

// ---------- drivers ----------
const NUS = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NUS_TX = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';
let stopFn = null;
export const gnssSupport = () => ({ system: !!navigator.geolocation, ble: !!navigator.bluetooth, serial: !!navigator.serial, ws: true });

function startSystem() {
  const id = navigator.geolocation.watchPosition((p) => {
    const a = p.coords.accuracy;
    emit({ lat: p.coords.latitude, lon: p.coords.longitude, alt: p.coords.altitude, hrms: a, vrms: p.coords.altitudeAccuracy, fix: 'sys', est: true, epoch: state.epoch + 1, ts: Date.now(), error: null });
  }, (e) => emit({ error: e.message || 'GPS' }), { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
  return () => navigator.geolocation.clearWatch(id);
}

async function startBle() {
  const dev = await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: [NUS] });
  const srv = await dev.gatt.connect();
  const ch = await (await srv.getPrimaryService(NUS)).getCharacteristic(NUS_TX);
  const dec = new TextDecoder();
  ch.addEventListener('characteristicvaluechanged', (e) => feedNmea(dec.decode(e.target.value)));
  await ch.startNotifications();
  dev.addEventListener('gattserverdisconnected', () => emit({ connected: false, error: 'BLE disconnected' }));
  emit({ deviceName: dev.name || '' });
  return () => dev.gatt.connected && dev.gatt.disconnect();
}

async function startSerial(baud) {
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate: baud });
  const reader = port.readable.getReader();
  const dec = new TextDecoder();
  let alive = true;
  (async () => {
    try { while (alive) { const { value, done } = await reader.read(); if (done) break; feedNmea(dec.decode(value)); } } catch (e) { if (alive) emit({ connected: false, error: String(e.message || e) }); }
  })();
  return async () => { alive = false; try { await reader.cancel(); reader.releaseLock(); await port.close(); } catch { /* port gone */ } };
}

function startWs(url) {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    ws.onopen = () => resolve(() => ws.close());
    ws.onerror = () => reject(new Error('WebSocket'));
    ws.onclose = () => state.mode === 'ws' && state.connected && emit({ connected: false, error: 'WebSocket closed' });
    ws.onmessage = async (e) => feedNmea(typeof e.data === 'string' ? e.data : await e.data.text());
  });
}

// demo receiver: real NMEA sentences (RTK fix, 18 sats) around a start point
function startSim(center) {
  const s = (b) => `$${b}*${nmeaChecksum(b)}\r\n`;
  const ddm = (v, lat) => { const a = Math.abs(v), d = Math.floor(a), m = (a - d) * 60; return [`${String(d).padStart(lat ? 2 : 3, '0')}${m.toFixed(7).padStart(10, '0')}`, lat ? (v < 0 ? 'S' : 'N') : (v < 0 ? 'W' : 'E')]; };
  let k = 0;
  const tick = () => {
    k++;
    const n = () => (Math.random() - 0.5) * 2e-7;
    const [la, ns] = ddm(center.lat + n(), true), [lo, ew] = ddm(center.lng + n(), false);
    const sq = localStorage.getItem('giss_gnss_sim_q'), t = new Date().toISOString().slice(11, 19).replace(/:/g, '') + '.00';
    const q = sq === 'float' || k < 3 ? 5 : sq === 'gps' ? 1 : 4, gst = sq === 'noisy' ? '0.06,0.07,0.12' : q === 4 ? '0.009,0.011,0.018' : '0.12,0.14,0.25';
    feedNmea(s(`GNGSA,A,3,02,05,12,15,18,24,25,29,,,,,1.2,0.7,1.0,1`) + s(`GLGSV,2,1,06`) + s(`GAGSV,2,1,05`)
      + s(`GNGGA,${t},${la},${ns},${lo},${ew},${q},18,0.7,${(512.84 + (Math.random() - 0.5) * 0.02).toFixed(3)},M,47.312,M,1.0,0000`)
      + s(`GNGST,${t},0.9,0.010,0.008,30.0,${gst}`));
  };
  emit({ sim: true });
  tick();
  const id = setInterval(tick, 1000);
  return () => clearInterval(id);
}

export async function startGnss(cfg, center) {
  await stopGnss();
  buf = '';
  emit({ connected: false, error: null, fix: null, lat: null, lon: null, alt: null, sats: null, hdop: null, hrms: null, vrms: null, geoid: null, systems: [], mode: cfg.mode, deviceName: '', sim: false });
  try {
    if (cfg.mode === 'system') stopFn = startSystem();
    else if (cfg.mode === 'ble') stopFn = await startBle();
    else if (cfg.mode === 'serial') stopFn = await startSerial(+cfg.baud);
    else stopFn = cfg.sim ? startSim(center) : await startWs(cfg.url);
    emit({ connected: true });
    return true;
  } catch (e) {
    emit({ connected: false, error: String(e.message || e) });
    return false;
  }
}
export async function stopGnss() {
  const f = stopFn; stopFn = null;
  emit({ connected: false, error: '' });
  if (f) await f();
}

// ---------- averaging & meta ----------
// n fresh epochs; reports worst fix, fix changes and whether the receiver stalled / disconnected
export function averageGnss(n) {
  return new Promise((resolve) => {
    const got = [], fix0 = state.fix;
    const done = (lost) => {
      unsub(); clearTimeout(to);
      const list = got.length ? got : gnssLive(state) ? [state] : [];
      const avg = (k) => { const v = list.map((x) => x[k]).filter((x) => x != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
      const hr = list.map((x) => x.hrms).filter((x) => x != null), vr = list.map((x) => x.vrms).filter((x) => x != null);
      const worst = list.reduce((w, x) => (FIX_RANK[x.fix] ?? 0) < (FIX_RANK[w] ?? 0) ? x.fix : w, fix0);
      resolve({ ...state, lat: avg('lat'), lon: avg('lon'), alt: avg('alt'), epochs: list.length, maxHrms: hr.length ? Math.max(...hr) : null, maxVrms: vr.length ? Math.max(...vr) : null,
        fix: worst, fixChanged: list.some((x) => x.fix !== fix0), incomplete: !!lost || list.length < n });
    };
    let last = state.epoch;
    const unsub = subscribeGnss((s) => {
      if (!s.connected) return done(true);
      if (s.epoch !== last && s.lat != null) { last = s.epoch; got.push(s); if (got.length >= n) done(); }
    });
    const to = setTimeout(() => done(true), n * 1000 + maxAgeMs());
    if (n <= 1 && gnssLive(state)) { got.push(state); done(); }
  });
}

const r3 = (v) => (v == null ? null : Math.round(v * 1000) / 1000);
// q: { profile: survey|plan, qs: ok|exception|plan|draft, reason }
export function gnssMeta(p, q = {}) {
  const c = getGnssCfg();
  const ground = p.alt != null ? p.alt - poleOff() : null;
  return {
    lat: +p.lat.toFixed(8), lon: +p.lon.toFixed(8), antennaAlt: r3(p.alt), ground: r3(ground), pipe: ground != null && +c.depth ? r3(ground - c.depth) : null,
    geoid: r3(p.geoid), fix: p.fix, acc: r3(p.maxHrms ?? p.hrms), vacc: r3(p.maxVrms ?? p.vrms), sats: p.sats ?? null, hdop: p.hdop ?? null, epochs: p.epochs || 1,
    antenna: +c.antenna || 0, poleRef: c.poleRef || 'antenna', depth: +c.depth || 0, device: state.sim ? 'SIMULATOR' : c.device || '', est: !!p.est, sim: !!state.sim, ts: Date.now(),
    profile: q.profile || (c.prec === 'plan' ? 'plan' : 'survey'), qs: q.qs || 'ok', ...(q.reason ? { reason: q.reason } : {}),
  };
}
// quality status of a stored point: ok | exception | plan | draft | sim
export const gnssQs = (g, draft) => (g?.sim ? 'sim' : draft ? 'draft' : g?.qs || (g ? 'ok' : 'manual'));
// one-line summary for PDF / lists
export const gnssLine = (g, t) => !g ? '' : [
  `GNSS ${FIX_SHORT[g.fix] || g.fix}`, t['qs_' + gnssQs(g)] ? `[${g.sim && g.qs === 'exception' ? `${t.qs_sim} · ${t.qs_exception}` : t['qs_' + gnssQs(g)]}${g.reason ? `: ${g.reason}` : ''}]` : '', g.profile ? `${t.gnssProfile} ${t['prMode_' + g.profile] || g.profile}` : '', g.acc != null ? `± ${g.acc.toFixed(3)} m` : t.gnssAccUnknown, g.sats != null ? `${g.sats} ${t.gnssSatsShort}` : '', g.hdop != null ? `HDOP ${g.hdop}` : '',
  g.ground != null ? `${t.gnssGround} ${g.ground.toFixed(3)} m ${t.gnssNN}` : '', g.pipe != null ? `${t.gnssPipe} ${g.pipe.toFixed(3)} m` : '',
  `${g.lat.toFixed(8)}, ${g.lon.toFixed(8)}`,
].filter(Boolean).join(' · ');
export const openGnss = () => window.dispatchEvent(new Event('giss-gnss-open'));
